import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";
import { MobileHeader } from "./MobileHeader";
import { MobileQuoteItemRow } from "./MobileQuoteItemRow";
import { QuoteActionsSheet } from "./QuoteActionsSheet";
import { QuoteSettingsSheet } from "./QuoteSettingsSheet";
import { QuoteItemEditSheet } from "./QuoteItemEditSheet";
import { getCurrencySymbol } from "../lib/currency";
import { DotsThreeVertical, PencilSimple, Plus, ReceiptX } from "@phosphor-icons/react";

export default function MobileQuoteDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { carts, refreshCarts, setActiveCartId } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const cart = carts.find((c) => c.id === id);

  const loadItems = useCallback(async () => {
    try {
      const { data } = await api.get(`/carts/${id}/items`);
      setItems(data);
    } catch {
      toast.error("Failed to load quotation items");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleDelete = async (itemId) => {
    setItems((prev) => prev.filter((it) => it.item_id !== itemId));
    try {
      await api.delete(`/carts/${id}/items/${itemId}`);
      refreshCarts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove item");
      loadItems();
    }
  };

  const handleQtyChange = async (itemId, newQty) => {
    setItems((prev) => prev.map((it) => (it.item_id === itemId ? { ...it, carton_qty: newQty, total_qty: newQty * it.pcs_per_ctn, total_amount: Math.round(newQty * it.pcs_per_ctn * (it.final_price || 0) * 100) / 100 } : it)));
    try {
      await api.put(`/carts/${id}/items/${itemId}`, { carton_qty: newQty });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update quantity");
      loadItems();
    }
  };

  const handleAddProducts = () => {
    setActiveCartId(id);
    navigate("/m/catalog");
  };

  if (!cart) {
    return (
      <div data-testid="mobile-quote-detail-screen">
        <MobileHeader title={t("mobile.quotation")} onBack={true} />
        <div className="text-center py-16 text-sm text-[#78716C]">{t("mobile.loading")}</div>
      </div>
    );
  }

  const symbol = getCurrencySymbol(cart.currency);
  const totalAmount = items.reduce((sum, it) => sum + (it.total_amount || 0), 0);

  return (
    <div data-testid="mobile-quote-detail-screen">
      <MobileHeader
        title={cart.name}
        subtitle={`${cart.customer_name || t("mobile.no_customer")} · ${cart.currency}${cart.sample_warehouse_name ? ` · ${cart.sample_warehouse_name}` : ""}`}
        onBack={true}
        rightSlot={
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => setSettingsOpen(true)}
              aria-label="Edit quotation"
              data-testid="mobile-quote-detail-edit-button"
              className="press-scale w-9 h-9 rounded-full bg-[#E7E5E4]/70 flex items-center justify-center text-[#1C1917]"
            >
              <PencilSimple size={16} />
            </button>
            <button
              onClick={() => setActionsOpen(true)}
              aria-label="Quote actions"
              data-testid="mobile-quote-detail-actions-button"
              className="press-scale w-9 h-9 rounded-full bg-[#1E3A8A] flex items-center justify-center text-white"
            >
              <DotsThreeVertical size={18} weight="bold" />
            </button>
          </div>
        }
      />

      <div className="px-4 pt-3">
        <div className="bg-[#1E3A8A] rounded-[16px] px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-white/60 text-xs uppercase tracking-wide font-semibold">{t("mobile.total_value")}</p>
            <p className="font-display text-xl text-white mt-0.5">{symbol}{totalAmount.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-xs uppercase tracking-wide font-semibold">{t("mobile.coefficient")}</p>
            <p className="font-display text-xl text-white mt-0.5">{cart.price_coefficient?.toFixed(2)}x</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 flex items-center justify-between">
        <h2 className="text-base font-medium text-[#1C1917]">{t("mobile.items", { count: items.length })}</h2>
        <button
          onClick={handleAddProducts}
          data-testid="mobile-quote-detail-add-products"
          className="press-scale flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] bg-[#1E3A8A]/8 rounded-full px-3.5 py-1.5"
        >
          <Plus size={14} weight="bold" /> {t("mobile.add_products")}
        </button>
      </div>

      <div className="px-4 pt-3 space-y-2 pb-4">
        {!loading && items.length === 0 && (
          <div className="text-center py-16 rounded-[20px] border border-dashed border-[#E7E5E4]" data-testid="mobile-quote-detail-empty">
            <ReceiptX size={30} weight="duotone" className="text-[#78716C] mx-auto mb-2" />
            <p className="text-sm text-[#78716C] mb-4">{t("mobile.no_items")}</p>
            <button onClick={handleAddProducts} className="text-sm font-semibold text-[#1E3A8A]">{t("mobile.browse_catalog")}</button>
          </div>
        )}
        {items.map((item) => (
          <MobileQuoteItemRow
            key={item.item_id}
            item={item}
            currency={cart.currency}
            onDelete={handleDelete}
            onQtyChange={handleQtyChange}
            onEdit={setEditingItem}
          />
        ))}
      </div>

      <QuoteItemEditSheet item={editingItem} cart={cart} onOpenChange={(o) => !o && setEditingItem(null)} onSaved={loadItems} />

      <QuoteActionsSheet open={actionsOpen} onOpenChange={setActionsOpen} cart={cart} items={items} />
      <QuoteSettingsSheet open={settingsOpen} onOpenChange={setSettingsOpen} cart={cart} onUpdated={() => { refreshCarts(); loadItems(); }} />
    </div>
  );
}
